import http from "./http"
import { getToken } from './auth'
import i18n from '../i18n/i18n'
import { translateApiMessage } from '../i18n/apiMessages'

const base = 'documents'

const authHeaders = () => ({ 'Authorization': 'Bearer ' + getToken() })

// Reads a PDF/image response as a Blob, turning an error JSON body into a
// thrown Error so callers can show the API's message.
const readFileResponse = async (response, fallback) => {
    const contentType = response.headers?.['content-type'] || ''
    if (response.status !== 200 || contentType.includes('application/json')) {
        let message = fallback
        try {
            const body = JSON.parse(await response.data.text())
            message = translateApiMessage(body?.message || body?.error) || message
        } catch (_) { /* The server did not return JSON. */ }
        throw new Error(message)
    }
    return response.data
}

/** All documents of a job (Invoice is a virtual live row; Receipt / Work Order are stored rows). */
export async function listJobDocuments(jobId) {
    const { data } = await http.get(`${base}/jobs/${jobId}`, { headers: authHeaders() })
    return data
}

/** Line items + customer name the pickup receipt checklist starts from. */
export async function getReceiptPrefill(jobId) {
    const { data } = await http.get(`${base}/jobs/${jobId}/pickup-receipt/prefill`, { headers: authHeaders() })
    return data
}

/** Create/replace the digital pickup receipt (items, notes, signature PNG data URL). */
export async function createPickupReceipt(jobId, payload) {
    const { data } = await http.post(`${base}/jobs/${jobId}/pickup-receipt`, payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
    })
    return data
}

/** Inline PDF of one document type (INVOICE / WORK_ORDER render live; PICKUP_RECEIPT is the stored file). */
export async function getDocumentPdf(jobId, type, { source } = {}) {
    const response = await http.get(`${base}/jobs/${jobId}/${type}/pdf`, {
        headers: authHeaders(),
        params: source ? { source } : undefined,
        responseType: 'blob'
    })
    return readFileResponse(response, i18n.t('jobDocuments.documentNotPrepared'))
}

/** Print-ready blank paper receipt (A4 or A5) prefilled with job + customer details. */
export async function getBlankReceiptPdf(jobId, size = 'A4') {
    const response = await http.get(`${base}/jobs/${jobId}/pickup-receipt/blank`, {
        headers: authHeaders(),
        params: { size },
        responseType: 'blob'
    })
    return readFileResponse(response, i18n.t('jobDocuments.blankReceiptNotPrepared'))
}

/** Upload a photo/scan (image or PDF) of a signed paper receipt as the raw request body. */
export async function uploadReceiptScan(jobId, file) {
    const { data } = await http.post(`${base}/jobs/${jobId}/pickup-receipt/scan`, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream', ...authHeaders() }
    })
    return data
}

/** Store work order completion / confirmation details (also creates the row + number). */
export async function saveWorkOrder(jobId, payload = {}) {
    const { data } = await http.post(`${base}/jobs/${jobId}/work-order`, payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
    })
    return data
}

export async function createDocumentShareLink(documentId) {
    const { data } = await http.post(`${base}/${documentId}/share-link`, {}, { headers: authHeaders() })
    return data
}

export async function emailDocument(documentId, { email, message }) {
    const { data } = await http.post(`${base}/${documentId}/email`, { email, message }, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
    })
    return data
}
